import { Component, ElementRef, QueryList, ViewChildren } from "@angular/core";
import { BackButtonComponent } from "../../../back-button/back-button.component";
import { NgIf } from "@angular/common";
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { SharedModule } from "../../../../modules/shared/shared.module";
import { Subject, takeUntil } from "rxjs";
import { ApiService } from "../../../../services/api.service";
import { ToasterService } from "../../../../services/toaster.service";
import { LoadingService } from "../../../../services/loading.service";
import { ActivatedRoute, Router } from "@angular/router";
import { Commodity, IMDGDetail } from "../../../../models/commodity-master.interface";
import { AutocompleteDropdownComponent } from "../../../autocomplete-dropdown/autocomplete-dropdown.component";
import commodityMasterConfig from '../config/commodityFormconfig.json';
import { DynamicFormComponent } from '../../../dynamic-form/dynamic-form.component';
import { DynamicForm } from "../../../../models/dynamic-form";
import { DynamicFormService } from '../../../../services/dynamic-form.service';
import { PageCloseButtonComponent } from "../../../common/close-button/page-close-button.component";


@Component({
  selector: 'app-manage-commodity',
  standalone: true,
  imports: [
    BackButtonComponent,
    NgIf,
    ReactiveFormsModule,
    SharedModule,
    AutocompleteDropdownComponent,
    DynamicFormComponent,
    PageCloseButtonComponent
],
  templateUrl: './manage-commodity.component.html',
  styleUrl: './manage-commodity.component.scss',
})
export class ManageCommodityComponent {
  readOnlyMode = true;
  masterEditingEnabled = false;
  showImdgForm: boolean = false;
  masterForm: FormGroup;
  masterId: string = '';
  basicInformationConfig: DynamicForm;
  imdgDetailsConfig:DynamicForm;

  @ViewChildren('formField') formFields!: QueryList<ElementRef>;
  private destroy$ = new Subject<void>();
  public masterDataResponse?: Commodity;

  constructor(
    private dynamicFormService :DynamicFormService,
    private fb: FormBuilder,
    private api: ApiService,
    private toasterService: ToasterService,
    private loadingService: LoadingService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.masterForm = fb.group({});

    this.basicInformationConfig = commodityMasterConfig.commodityFormElements;
    this.imdgDetailsConfig = commodityMasterConfig.imdgDetailsFormElements;

    this.dynamicFormService.addControlsToForm(
      this.masterForm,
      this.basicInformationConfig
    );
  }

  ngOnInit(): void {
    this.masterId = this.route.snapshot.paramMap.get('commodityId') || '';
    console.log(this.masterId);
    this.setupComponentMode();
    if (this.masterId) {
      this.loadMasterData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  get imdgForm(): FormGroup {
    return this.masterForm.get('imdgDetails') as FormGroup;
  }
  createImdgFormGroup(): void {
    this.dynamicFormService.addControlsToForm(
      this.imdgForm,
      this.imdgDetailsConfig
    );
  }

  private setupComponentMode(): void {
    if (this.masterId) {
      const path = this.route.snapshot.url[0].path;
      this.masterEditingEnabled = path === 'edit';
      this.readOnlyMode = !this.masterEditingEnabled;
      if (this.readOnlyMode) {
        this.masterForm.disable();
      }
    } else {
      this.readOnlyMode = false;
      this.masterEditingEnabled = false;
    }
  }

  private loadMasterData(): void {
    this.loadingService.show();
    this.api
      .getCommodityMaster(this.masterId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const commodityData: Commodity = res.data;
          this.masterDataResponse = { ...commodityData };

          this.patchFormWithMasterData(commodityData);
          this.loadingService.hide();
        },
        error: (err) => {
          console.error('Error loading commodity data:', err);
          this.toasterService.error('Failed to load commodity data');
          this.loadingService.hide();
        },
      });
  }

  private patchFormWithMasterData(commodity: Commodity): void {
       if (commodity.imdgDetails !== null) {
         this.addImdgControlCreateFormGroup();
       }
    this.masterForm.patchValue({
      ...commodity,
      hasImdgDetails : (commodity.imdgDetails !== null),
      commodityCategory: {
        key: commodity.commodityCategory?.id,
        value: commodity.commodityCategory?.name,
      },
    });
  }

  onCheckboxChange(e: any) {
    console.log('onCheckboxChange');
    this.showImdgForm = e.target.checked;
  }

  submitMasterForm(): void {
    if (this.masterForm.valid) {
      const formValue = this.masterForm.value;
      const masterData: Commodity = {
        ...formValue,
        hsnCode: formValue.hsnCode ?? "",
        commodityCategory: formValue.commodityCategory?.key,
        imdgDetails: formValue.hasImdgDetails ? formValue.imdgDetails : null,
      };
      // masterData.commodityCategory = this.masterDataResponse?.commodityCategory || masterData.commodityCategory;
      masterData.active = true; //always create new master resource with active=true

      this.loadingService.show();
      const action$ = this.masterEditingEnabled
        ? this.api.updateCommodityMaster(masterData, this.masterId)
        : this.api.addCommodityMaster(masterData);

      action$.pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          const message = this.masterEditingEnabled
            ? `Commodity ${masterData.name} has been updated!`
            : `Commodity ${masterData.name} has been added!`;
          this.toasterService.success(message);
          this.loadingService.hide();
          this.goBack();
        },
        error: (err) => {
          this.toasterService.error(err.error.errorDesc || 'An error occurred');
          console.error('Error submitting form:', err);
          this.loadingService.hide();
        },
      });
    } else {
      this.markFormGroupTouched(this.masterForm);
      this.scrollToFirstInvalidControl();
    }
  }

  private markFormGroupTouched(formGroup: FormGroup | FormArray): void {
    Object.values(formGroup.controls).forEach((control) => {
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupTouched(control);
      } else {
        control.markAsTouched();
      }
    });
  }

  scrollToFirstInvalidControl(): void {
    const firstInvalidControl = this.formFields.find((element) => {
      const nativeElement = element.nativeElement as HTMLElement;
      return nativeElement.classList.contains('ng-invalid');
    });

    if (firstInvalidControl) {
      firstInvalidControl.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      (firstInvalidControl.nativeElement as HTMLElement).focus();
    }
  }

  goBack(): void {
    this.router.navigateByUrl('/manage-masters/commodities');
  }

  addImdgControlCreateFormGroup(){
    this.masterForm.addControl('imdgDetails', this.fb.group({}));
    this.createImdgFormGroup();

    if (this.readOnlyMode) {
      this.imdgForm.disable();
    }
  }
  handleValueChange(event: any): void {
    const { controlName, result } = event;

    if (controlName === 'hasImdgDetails' && result === true) {
      // Here We will Handle the imdgDetails Toggle;

      console.log('hasImdgDetails ', result);
       this.addImdgControlCreateFormGroup();
    }
  }
}
